<script setup lang="ts">
import { computed } from 'vue'
import { ChevronDownIcon, ChevronUpIcon, SearchIcon, XIcon } from '@lucide/vue'

import {
  DSInputGroup,
  DSInputGroupAddon,
  DSInputGroupButton,
  DSInputGroupInput,
  DSInputGroupText,
} from '@ds'

type Props = {
  modelValue: string
  matchCount: number
  activeMatchIndex: number
  searchPlaceholder: string
}

const { modelValue, matchCount, activeMatchIndex, searchPlaceholder } = defineProps<Props>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
  prev: []
  next: []
}>()

const hasQuery = computed(() => modelValue.length > 0)
const countLabel = computed(() => matchCount ? `${activeMatchIndex + 1} of ${matchCount}` : '0 of 0')
</script>

<template>
  <DSInputGroup size="sm">
    <DSInputGroupAddon align="inline-start">
      <SearchIcon aria-hidden="true" />
    </DSInputGroupAddon>
    <DSInputGroupInput
      :model-value="modelValue"
      :placeholder="searchPlaceholder"
      @update:model-value="emit('update:modelValue', String($event))"
      @keydown.enter.exact.prevent="emit('next')"
      @keydown.enter.shift.prevent="emit('prev')"
      @keydown.down.prevent="emit('next')"
      @keydown.up.prevent="emit('prev')"
    />
    <DSInputGroupAddon
      v-if="hasQuery"
      align="inline-end"
      class="pr-2.25 gap-0"
    >
      <DSInputGroupText class="tabular-nums">
        {{ countLabel }}
      </DSInputGroupText>
      <template v-if="matchCount > 1">
        <DSInputGroupButton
          type="button"
          title="Previous match"
          @click="emit('prev')"
        >
          <ChevronUpIcon aria-hidden="true" />
        </DSInputGroupButton>
        <DSInputGroupButton
          type="button"
          title="Next match"
          @click="emit('next')"
        >
          <ChevronDownIcon aria-hidden="true" />
        </DSInputGroupButton>
      </template>
      <DSInputGroupButton
        type="button"
        title="Clear"
        @click="emit('update:modelValue', '')"
      >
        <XIcon aria-hidden="true" />
      </DSInputGroupButton>
    </DSInputGroupAddon>
  </DSInputGroup>
</template>
